import {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import toast from 'react-hot-toast';
import {MCPOAuthApi, ProjectPermission} from '../../../api/authority/generated/index.ts';
import Button from '../../../components/Button.jsx';
import SuperadminIndicator from '../../../components/auth/SuperadminIndicator.jsx';
import {GlobalContext} from '../../../utils/context/GlobalContextProvider.jsx';
import {hasSubscriptionFeature} from '../../../utils/subscriptionFeatures.js';

export function hasVisibleProjectMcpAccess(project, hasProjectAccess, isAdministrator = false) {
    return (hasProjectAccess === true || isAdministrator === true) && (isAdministrator === true || (
        project?.subscription?.status === 'active' && hasSubscriptionFeature(project, 'mcp_access')
    ));
}

export function getProjectSettingsAccess(project, hasPermission, isProjectMember, isAdministrator = false) {
    return {
        canEditProfile: hasPermission(ProjectPermission.ProjectProfile, project),
        canViewBilling: hasPermission(ProjectPermission.ProjectBilling, project),
        canViewMcp: hasVisibleProjectMcpAccess(project, isProjectMember || isAdministrator, isAdministrator),
    };
}

export async function loadProjectMcpDiscovery(api) {
    return (await api.mCPProtectedResourceMetadata()).resource;
}

export function ProjectMcpDiscoveryPanel({resource, error, isAdministrator = false, onRefresh, onCopyResource, onManageConnections, t}) {
    return <section aria-label={t('project.mcp.box-title', {defaultValue: 'MCP pro AI asistenty'})} className={isAdministrator ? 'app-surface relative rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-500/30 dark:bg-violet-500/10' : undefined}>
        {isAdministrator && <SuperadminIndicator className="absolute right-3 top-3"/>}
        <p className={`mb-4 text-sm text-slate-600 dark:text-slate-300 ${isAdministrator ? 'pr-10' : ''}`}>{isAdministrator
            ? t('project.mcp.description-superadmin', {defaultValue: 'Jako superadministrátor používáte jedno společné MCP připojení pro všechny projekty, včetně projektů bez MCP v balíčku. Datixo oprávnění znovu ověří při každém dotazu.'})
            : t('project.mcp.description', {defaultValue: 'Tento projekt je dostupný přes jedno společné MCP připojení vašeho účtu. AI klient vždy uvede konkrétní projekt a Datixo při každém dotazu znovu ověří váš aktuální přístup.'})}</p>
        {resource === null && !error && <p role="status" className="text-sm text-slate-500">{t('project.mcp.loading', {defaultValue: 'Načítám MCP adresu…'})}</p>}
        {error && <div role="alert" className="app-surface rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-datixo-negative">
            <p>{t('project.mcp.error', {defaultValue: 'MCP adresu se nepodařilo načíst.'})}</p>
            <Button variant="outline-red" size="small" className="mt-3" onClick={onRefresh}>{t('project.mcp.retry', {defaultValue: 'Zkusit znovu'})}</Button>
        </div>}
        {resource && <div>
            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('project.mcp.url', {defaultValue: 'Společná MCP URL'})}</div>
            <div className="app-surface mt-2 flex flex-col items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900 sm:flex-row">
                <span className="min-w-0 flex-1 break-all font-mono text-sm text-slate-800 dark:text-slate-200">{resource}</span>
                <Button variant="outline-primary" size="small" className="shrink-0" onClick={onCopyResource} aria-label={t('copy-to-clipboard')}>{t('copy', {defaultValue: 'Kopírovat'})}</Button>
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('project.mcp.url-help', {defaultValue: 'Stejnou adresu vložíte do AI klienta jen jednou. Po přihlášení uvidí všechny projekty dostupné pod vaším účtem.'})}</p>
            <Button variant="outline-primary" className="mt-4" onClick={onManageConnections}>{t('project.mcp.manage-account-connections', {defaultValue: 'Spravovat AI připojení účtu'})}</Button>
        </div>}
    </section>;
}

function EntitledProjectMcpDiscovery({isAdministrator}) {
    const {t} = useTranslation();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const apiRef = useRef(null);
    const requestIdRef = useRef(0);
    const [resource, setResource] = useState(null);
    const [error, setError] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    if (apiRef.current === null) apiRef.current = new MCPOAuthApi(getAuthorityConfiguration());

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setResource(null);
        setError(false);
        loadProjectMcpDiscovery(apiRef.current).then((value) => {
            if (requestIdRef.current === requestId) setResource(value);
        }).catch(() => {
            if (requestIdRef.current === requestId) setError(true);
        });
        return () => { requestIdRef.current += 1; };
    }, [refreshKey]);

    const copy = () => Promise.resolve().then(() => navigator.clipboard.writeText(resource)).then(() => toast.success(t('copy-to-clipboard-success'))).catch(() => toast.error(t('copy-to-clipboard-failed')));
    const manage = () => window.dispatchEvent(new CustomEvent('datixo:open-account-settings', {detail: {section: 'mcp'}}));
    return <ProjectMcpDiscoveryPanel resource={resource} error={error} isAdministrator={isAdministrator} onRefresh={() => setRefreshKey((value) => value + 1)} onCopyResource={copy} onManageConnections={manage} t={t}/>;
}

export default function ProjectMcpGrants({project, isAdministrator = false}) {
    if (!hasVisibleProjectMcpAccess(project, true, isAdministrator)) return null;
    return <EntitledProjectMcpDiscovery key={project.id} isAdministrator={isAdministrator}/>;
}
